import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { colors } from '../theme';
import SendIcon from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    siteAddress: '',
    message: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Capture UTM parameters on component mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmParams = {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      utm_term: params.get('utm_term') || '',
    };
    
    if (Object.values(utmParams).some(val => val)) {
      setFormData(prev => ({
        ...prev,
        ...utmParams
      }));
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Replace with your actual Lambda endpoint
      const response = await fetch(
        'https://7thci7uaevukaluqxheqyckjfi0egwil.lambda-url.ap-southeast-1.on.aws/',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        setSnackbar({
          open: true,
          message:
            'Thank you! Your message has been sent successfully. We will contact you soon.',
          severity: 'success',
        });
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          siteAddress: '',
          message: '',
          utm_source: formData.utm_source,
          utm_medium: formData.utm_medium,
          utm_campaign: formData.utm_campaign,
          utm_content: formData.utm_content,
          utm_term: formData.utm_term,
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          'Sorry, there was an error sending your message. Please try again or contact us directly.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 6, md: 8 },
        background: `linear-gradient(135deg, ${colors.black} 0%, #1a1a1a 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `
            linear-gradient(${colors.yellow} 1px, transparent 1px),
            linear-gradient(90deg, ${colors.yellow} 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 800,
              mb: 2,
              color: colors.white,
            }}
          >
            Get in{' '}
            <Box component="span" sx={{ color: colors.yellow }}>
              Touch
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: colors.white,
              opacity: 0.9,
              maxWidth: '700px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            Ready to power up your EV charging infrastructure? Contact us today
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Contact Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ height: '100%' }}>
              <Typography
                variant="h5"
                sx={{
                  color: colors.yellow,
                  fontWeight: 700,
                  mb: 3,
                }}
              >
                Contact Information
              </Typography>

              {/* Contact Cards */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: `2px solid ${colors.green}40`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                >
                  <LocationOnIcon sx={{ color: colors.green, fontSize: 30 }} />
                  <Box>
                    <Typography
                      sx={{
                        color: colors.white,
                        fontWeight: 700,
                        mb: 0.5,
                      }}
                    >
                      Office Location
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.white,
                        opacity: 0.8,
                        fontSize: '0.9rem',
                      }}
                    >
                      Kaki Bukit Road 1
                      <br />
                      Singapore 415934
                    </Typography>
                  </Box>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: `2px solid ${colors.yellow}40`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                >
                  <EmailIcon sx={{ color: colors.yellow, fontSize: 30 }} />
                  <Box>
                    <Typography
                      sx={{
                        color: colors.white,
                        fontWeight: 700,
                        mb: 0.5,
                      }}
                    >
                      Email Us
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.white,
                        opacity: 0.8,
                        fontSize: '0.9rem',
                      }}
                    >
                      info@solacharge.net
                      <br />
                      sales@solacharge.net
                    </Typography>
                  </Box>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    border: `2px solid ${colors.green}40`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                >
                  <PhoneIcon sx={{ color: colors.green, fontSize: 30 }} />
                  <Box>
                    <Typography
                      sx={{
                        color: colors.white,
                        fontWeight: 700,
                        mb: 0.5,
                      }}
                    >
                      Call Us
                    </Typography>
                    <Typography
                      sx={{
                        color: colors.white,
                        opacity: 0.8,
                        fontSize: '0.9rem',
                      }}
                    >
                      +65 8188 7716
                      <br />
                      Mon-Fri: 9AM - 6PM
                    </Typography>
                  </Box>
                </Paper>
              </Box>

              {/* Licensed Badge */}
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  borderRadius: 3,
                  bgcolor: `${colors.yellow}20`,
                  border: `2px solid ${colors.yellow}`,
                  textAlign: 'center',
                }}
              >
                <Typography
                  sx={{
                    color: colors.white,
                    fontWeight: 700,
                    fontSize: '1rem',
                  }}
                >
                  Licensed LTA EVCO Operator
                </Typography>
                <Typography
                  sx={{
                    color: colors.white,
                    fontSize: '0.85rem',
                    opacity: 0.9,
                    mt: 0.5,
                  }}
                >
                  Certified & Compliant
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 4,
                bgcolor: colors.white,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: colors.black,
                }}
              >
                Get a Free Consultation
              </Typography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: colors.green,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.green,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Company Name"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: colors.green,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.green,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Email Address"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: colors.green,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.green,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: colors.green,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.green,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Site Address"
                      name="siteAddress"
                      value={formData.siteAddress}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: colors.green,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.green,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      rows={4}
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      variant="outlined"
                      placeholder="Tell us about your EV charging requirements..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': {
                            borderColor: colors.green,
                          },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: colors.green,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={loading}
                      endIcon={
                        loading ? (
                          <CircularProgress size={20} sx={{ color: colors.black }} />
                        ) : (
                          <SendIcon />
                        )
                      }
                      sx={{
                        bgcolor: colors.yellow,
                        color: colors.black,
                        py: 1.5,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        '&:hover': {
                          bgcolor: colors.lightYellow,
                        },
                        '&:disabled': {
                          bgcolor: '#ccc',
                        },
                      }}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ContactSection;
